import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import { toast } from "sonner";
import axios from "axios";
import {
  CreditCard, Check, X, Crown, Zap, Star, Building2,
  ChevronRight, Loader2, History, ArrowRight, Brain, MessageSquare,
  FileText, Shield, BarChart3, Truck, Bell, Sparkles
} from "lucide-react";
import { Button } from "../components/ui/button";

const PLAN_ICONS = { basique: Star, starter: Zap, pro: Crown, complet: Building2 };

const PLAN_COLORS = {
  basique: { ring: "ring-slate-400/30", badge: "bg-white/50/10 text-slate-400", btn: "bg-slate-600 hover:bg-white/50", gradient: "from-slate-500/5 to-slate-900/60" },
  starter: { ring: "ring-sky-400/30", badge: "bg-sky-500/10 text-sky-400", btn: "bg-sky-600 hover:bg-sky-500", gradient: "from-sky-500/5 to-slate-900/60" },
  pro: { ring: "ring-amber-400/30", badge: "bg-amber-500/100/10 text-amber-400", btn: "bg-amber-500/100 hover:bg-amber-400", gradient: "from-amber-500/5 to-slate-900/60", popular: true },
  complet: { ring: "ring-emerald-400/30", badge: "bg-emerald-500/10 text-emerald-400", btn: "bg-emerald-600 hover:bg-emerald-500", gradient: "from-emerald-500/5 to-slate-900/60" },
};

const FEATURES = [
  { key: "max_invoices_month", label: "Factures/mois", icon: FileText, fmt: v => v === -1 ? "Illimite" : v },
  { key: "max_products", label: "Produits max", icon: FileText, fmt: v => v === -1 ? "Illimite" : v },
  { key: "import_excel_pdf", label: "Import Excel/PDF", icon: FileText },
  { key: "certificats_pdf", label: "Certificats PDF + QR", icon: Shield },
  { key: "export_pdf", label: "Export PDF", icon: FileText },
  { key: "export_excel", label: "Export Excel", icon: FileText },
  { key: "monthly_reports", label: "Rapports mensuels", icon: BarChart3 },
  { key: "smart_alerts", label: "Alertes intelligentes", icon: Bell },
  { key: "messagerie_assureurs", label: "Messagerie assureurs", icon: MessageSquare },
  { key: "expiry_alerts", label: "Alertes peremption", icon: Bell },
  { key: "ai_reports", label: "Rapports IA Claude", icon: Brain },
  { key: "ai_dashboard", label: "Tableau de bord IA", icon: Sparkles },
  { key: "ia_predictive", label: "IA Prédictive", icon: Brain },
  { key: "tonnage_analytics", label: "Analytics tonnage", icon: BarChart3 },
  { key: "check_scanning", label: "Scan de cheques", icon: CreditCard },
  { key: "delivery_scanning", label: "Scan bordereaux", icon: Truck },
  { key: "alerts_sms_wa", label: "SMS / WhatsApp", icon: Bell },
  { key: "benchmark_level", label: "Benchmarking", icon: BarChart3, fmt: v => v === "full" ? "Complet" : v === "commune" ? "Commune" : false },
  { key: "api_pos", label: "API Logiciels de caisse", icon: Shield },
  { key: "multi_sites", label: "Multi-sites", icon: Building2, fmt: v => v > 0 ? `${v} sites` : false },
];

const fmtPrice = (p) => p === 0 ? "Gratuit" : `${p.toLocaleString("fr-FR")} FCFA`;

export default function SubscriptionPage() {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(null);
  const [billing, setBilling] = useState("monthly");
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const [plansRes, curRes, histRes] = await Promise.all([
        axios.get(`${API}/subscriptions/plans`, { headers }),
        axios.get(`${API}/subscriptions/current`, { headers }),
        axios.get(`${API}/subscriptions/history`, { headers }),
      ]);
      setPlans(plansRes.data.plans || []);
      setCurrent(curRes.data);
      setHistory(histRes.data.transactions || []);
    } catch { }
    finally { setLoading(false); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // Check payment after redirect
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId && token) {
      setChecking(true);
      axios.get(`${API}/subscriptions/checkout/status/${sessionId}`, { headers })
        .then(res => {
          if (res.data.payment_status === "paid") {
            toast.success(`Abonnement ${res.data.plan_name} active !`);
            load();
          } else {
            toast.info("Paiement en attente de confirmation");
          }
        })
        .catch(() => toast.error("Erreur vérification paiement"))
        .finally(() => setChecking(false));
    }
  }, [searchParams, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubscribe = async (planId) => {
    try {
      const res = await axios.post(`${API}/subscriptions/checkout`, {
        plan_id: planId, billing_cycle: billing,
        origin_url: window.location.origin,
      }, { headers });
      if (res.data.url) window.location.href = res.data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (loading) return (
    <Layout>
      <div className="flex justify-center py-24">
        <div className="w-10 h-10 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" />
      </div>
    </Layout>
  );

  const currentPlanId = current?.plan_id || "basique";

  return (
    <Layout>
      <div data-testid="subscription-page" className="max-w-6xl mx-auto space-y-8 py-4">
        {/* Header */}
        <div className="text-center space-y-3">
          <h1 className="text-3xl font-black text-white">Choisissez votre formule</h1>
          <p className="text-sm text-slate-400 max-w-lg mx-auto">
            Des outils intelligents pour optimiser la gestion de votre pharmacie. Chaque plan est concu pour accompagner votre croissance.
          </p>
          {/* Billing toggle */}
          <div className="flex items-center justify-center gap-3 mt-4">
            <button onClick={() => setBilling("monthly")}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${billing === "monthly" ? "bg-sky-500 text-white" : "text-slate-400 hover:text-white"}`}>
              Mensuel
            </button>
            <button onClick={() => setBilling("yearly")}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${billing === "yearly" ? "bg-sky-500 text-white" : "text-slate-400 hover:text-white"}`}>
              Annuel <span className="text-emerald-400 text-xs ml-1">-17%</span>
            </button>
          </div>
        </div>

        {checking && (
          <div className="bg-sky-500/10 border border-sky-500/20 rounded-xl p-4 text-center">
            <Loader2 className="w-5 h-5 animate-spin text-sky-400 mx-auto mb-2" />
            <p className="text-sm text-sky-400">Vérification du paiement en cours...</p>
          </div>
        )}

        {/* Plans grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans.map(plan => {
            const colors = PLAN_COLORS[plan.id] || PLAN_COLORS.basique;
            const Icon = PLAN_ICONS[plan.id] || Star;
            const isCurrent = currentPlanId === plan.id;
            const price = billing === "yearly" ? plan.price_yearly : plan.price_monthly;
            const limits = plan.limits || {};

            return (
              <div key={plan.id} data-testid={`plan-${plan.id}`}
                className={`relative bg-gradient-to-br ${colors.gradient} border ${isCurrent ? "border-sky-500/40 ring-2 ring-sky-500/20" : "border-white/5"} rounded-2xl p-5 flex flex-col transition-all hover:border-white/10`}>
                {colors.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-3 py-1 bg-amber-500/100 text-black text-[10px] font-bold rounded-full uppercase tracking-wider">
                      Populaire
                    </span>
                  </div>
                )}
                {isCurrent && (
                  <div className="absolute -top-3 right-4">
                    <span className="px-3 py-1 bg-sky-500 text-white text-[10px] font-bold rounded-full">
                      Plan actuel
                    </span>
                  </div>
                )}

                <div className="flex items-center gap-2.5 mb-3 mt-1">
                  <div className={`w-9 h-9 rounded-xl ${colors.badge} flex items-center justify-center`}>
                    <Icon className="w-4.5 h-4.5" />
                  </div>
                  <h3 className="text-lg font-bold text-white">{plan.name}</h3>
                </div>

                <div className="mb-4">
                  <span className="text-2xl font-black text-white">{fmtPrice(price)}</span>
                  {price > 0 && <span className="text-xs text-slate-500 ml-1">/{billing === "yearly" ? "an" : "mois"}</span>}
                </div>

                <p className="text-xs text-slate-500 mb-4">{plan.description}</p>

                {/* Features */}
                <div className="flex-1 space-y-1.5 mb-4">
                  {FEATURES.map(f => {
                    const val = limits[f.key];
                    const formatted = f.fmt ? f.fmt(val) : val;
                    const included = formatted !== false && formatted !== 0 && formatted !== "none" && val !== false && val !== undefined;

                    return (
                      <div key={f.key} className="flex items-center gap-2 text-xs">
                        {included ? (
                          <Check className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                        ) : (
                          <X className="w-3.5 h-3.5 text-slate-600 flex-shrink-0" />
                        )}
                        <span className={included ? "text-slate-300" : "text-slate-600"}>
                          {f.label}
                          {typeof formatted === "string" && formatted !== "true" && included && (
                            <span className="text-slate-500 ml-1">({formatted})</span>
                          )}
                          {typeof formatted === "number" && included && (
                            <span className="text-slate-500 ml-1">({formatted})</span>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* CTA */}
                {isCurrent ? (
                  <div className="text-center py-2 px-4 rounded-xl bg-sky-500/10 border border-sky-500/20 text-sky-400 text-sm font-semibold">
                    Votre plan actuel
                  </div>
                ) : price === 0 ? (
                  <div className="text-center py-2 px-4 rounded-xl bg-slate-700/30 text-slate-500 text-sm">
                    Plan gratuit
                  </div>
                ) : (
                  <Button data-testid={`subscribe-${plan.id}`} onClick={() => handleSubscribe(plan.id)}
                    className={`w-full ${colors.btn} text-white font-semibold rounded-xl`}>
                    Souscrire <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        {/* Payment history */}
        <div className="bg-slate-900/60 border border-white/5 rounded-xl p-5">
          <button data-testid="toggle-history" onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-2 text-sm font-semibold text-slate-300 hover:text-white transition-colors w-full">
            <History className="w-4 h-4 text-sky-400" />
            Historique des paiements
            <ChevronRight className={`w-4 h-4 ml-auto transition-transform ${showHistory ? "rotate-90" : ""}`} />
          </button>

          {showHistory && (
            <div className="mt-4 space-y-2">
              {history.length === 0 ? (
                <p className="text-xs text-slate-500 py-4 text-center">Aucun paiement effectue</p>
              ) : history.map((t, i) => (
                <div key={t.id || t.created_at + '-' + i} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/60 border border-white/5">
                  <div>
                    <p className="text-xs font-medium text-white">{t.plan_name} - {t.billing_cycle}</p>
                    <p className="text-[10px] text-slate-500">{new Date(t.created_at).toLocaleDateString("fr-FR")}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-bold text-white">{fmtPrice(t.amount)}</p>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                      t.status === "completed" ? "bg-emerald-500/15 text-emerald-400" :
                      t.status === "pending" ? "bg-amber-500/100/15 text-amber-400" : "bg-red-500/100/15 text-red-400"
                    }`}>{t.status}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
